import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import * as S from './styles';
import TabsCustom from 'components/Tabs';
import { useMenuSelection } from 'global/MenuSelectionContext';
import { CommunityProfile } from 'pages/CommunityProfile';
import FormListPage from 'pages/FormListPage';
import Rooms from 'pages/Rooms';
import UnderConstruction from 'pages/UnderConstruction';
import { MenuItem, isFormExtras, isMeetingRoomExtras } from 'types/menuTypes';
import { hasImplementedComponent } from 'utils/componentMap';

interface ThirdLevelLayoutProps {
  children?: React.ReactNode;
}

const ThirdLevelLayout = ({ children }: ThirdLevelLayoutProps) => {
  const {
    firstLevelMenu,
    secondLevelMenu,
    thirdLevelMenu,
    selectMenuByDocumentId,
    activeDocumentId,
    currentLevel,
  } = useMenuSelection();
  const [activeTab, setActiveTab] = useState<string>('');
  const location = useLocation();
  const navigate = useNavigate();

  // Memoiza as tabs para evitar recálculos desnecessários
  const thirdLevelMenus = useMemo(() => secondLevelMenu?.subMenu || [], [secondLevelMenu?.subMenu]);

  // Verifica se estamos realmente em um menu de terceiro nível
  const isThirdLevel = useMemo(() => {
    const isThird = currentLevel === 'third';
    console.log('[ThirdLevelLayout] Verificando nível:', {
      currentLevel,
      isThird,
      hasSecondLevel: !!secondLevelMenu,
      hasThirdLevelMenus: thirdLevelMenus.length > 0,
    });
    return isThird;
  }, [currentLevel, secondLevelMenu, thirdLevelMenus]);

  // Função auxiliar para obter a chave correta da tab
  const getTabKey = useCallback((menu: MenuItem) => {
    const dynamicComponent = menu.feature?.dynamic_component;
    const isMeetingRoom =
      dynamicComponent === 'MEETING_ROOM' || dynamicComponent === 'MEETING_ROOM_ROOM';

    if (isMeetingRoom && menu.extras && isMeetingRoomExtras(menu.extras)) {
      return menu.extras.internal_id;
    }

    return menu.feature?.documentId || '';
  }, []);

  // Sincroniza a tab ativa com o menu selecionado
  useEffect(() => {
    if (!isThirdLevel) {
      console.log('[ThirdLevelLayout] Não é terceiro nível, ignorando sincronização de tab');
      return;
    }

    if (thirdLevelMenu) {
      const tabKey = getTabKey(thirdLevelMenu);

      if (tabKey && tabKey !== activeTab) {
        console.log('[ThirdLevelLayout] Sincronizando tab ativa com menu:', {
          tabKey,
          label: thirdLevelMenu.label,
          currentActiveTab: activeTab,
        });
        setActiveTab(tabKey);
      }
    }
  }, [thirdLevelMenu, activeTab, isThirdLevel, getTabKey]);

  // Inicializa a primeira tab automaticamente quando o menu muda
  useEffect(() => {
    if (!isThirdLevel) {
      console.log('[ThirdLevelLayout] Não é terceiro nível, ignorando inicialização de tab');
      return;
    }

    if (thirdLevelMenus.length > 0 && !activeTab) {
      // Tenta encontrar a tab que corresponde ao menu atual
      let targetTab = thirdLevelMenus[0]; // fallback para primeira tab

      if (thirdLevelMenu) {
        const currentTab = thirdLevelMenus.find(tab => {
          const tabKey = getTabKey(tab);
          const thirdLevelKey = getTabKey(thirdLevelMenu);

          // Para MEETING_ROOM, compara por internal_id ou path
          const isMeetingRoom =
            tab.feature?.dynamic_component === 'MEETING_ROOM' ||
            tab.feature?.dynamic_component === 'MEETING_ROOM_ROOM';

          if (isMeetingRoom) {
            return tabKey === thirdLevelKey || tab.path === thirdLevelMenu.path;
          }

          // Para outros tipos, compara por documentId ou path
          return (
            tab.feature?.documentId === thirdLevelMenu.feature?.documentId ||
            tab.path === thirdLevelMenu.path
          );
        });

        if (currentTab) {
          targetTab = currentTab;
          console.log('[ThirdLevelLayout] Encontrou tab atual:', targetTab.label);
        }
      }

      const tabKey = getTabKey(targetTab);

      if (tabKey) {
        console.log('[ThirdLevelLayout] Inicializando tab:', {
          tabKey,
          label: targetTab.label,
          path: targetTab.path,
          isCurrentTab: targetTab === thirdLevelMenus[0] ? 'primeira (fallback)' : 'atual',
        });
        setActiveTab(tabKey);
        if (firstLevelMenu && secondLevelMenu) {
          selectMenuByDocumentId(
            tabKey,
            true,
            [firstLevelMenu, secondLevelMenu, targetTab],
            targetTab.path
          );
        }
      }
    }
  }, [
    thirdLevelMenus,
    activeTab,
    firstLevelMenu,
    secondLevelMenu,
    thirdLevelMenu,
    selectMenuByDocumentId,
    isThirdLevel,
    getTabKey,
  ]);

  // Troca de tab pelo usuário
  const handleTabChange = useCallback(
    (tabKey: string) => {
      if (!isThirdLevel) {
        console.log('[ThirdLevelLayout] Não é terceiro nível, ignorando mudança de tab');
        return;
      }

      console.log('[ThirdLevelLayout] Mudando para tab:', tabKey);
      const selectedTab = thirdLevelMenus.find(tab => getTabKey(tab) === tabKey);

      if (selectedTab && firstLevelMenu && secondLevelMenu) {
        console.log('[ThirdLevelLayout] Atualizando menu com caminho completo:', {
          first: firstLevelMenu.label,
          second: secondLevelMenu.label,
          third: selectedTab.label,
          path: selectedTab.path,
        });

        setActiveTab(tabKey);
        selectMenuByDocumentId(
          tabKey,
          true,
          [firstLevelMenu, secondLevelMenu, selectedTab],
          selectedTab.path
        );
      } else {
        console.warn('[ThirdLevelLayout] Não foi possível mudar de tab - menus pai ausentes:', {
          hasFirstLevel: !!firstLevelMenu,
          hasSecondLevel: !!secondLevelMenu,
          selectedTab: selectedTab?.label,
        });
      }
    },
    [
      thirdLevelMenus,
      firstLevelMenu,
      secondLevelMenu,
      selectMenuByDocumentId,
      isThirdLevel,
      getTabKey,
    ]
  );

  // Renderização do conteúdo da tab baseada no dynamic_component
  const renderTabContent = useCallback(
    (tab: MenuItem, idx?: number) => {
      const dynamicComponent = tab.feature?.dynamic_component;
      const extras = tab.feature?.extras || null;

      console.log('[ThirdLevelLayout] Renderizando tab:', {
        label: tab.label,
        dynamicComponent,
        documentId: tab.feature?.documentId,
        internalId: extras && isMeetingRoomExtras(extras) ? extras.internal_id : null,
      });

      if (!dynamicComponent && !hasImplementedComponent(tab.feature?.documentId || '')) {
        return <UnderConstruction />;
      }

      switch (dynamicComponent) {
        case 'FORM':
          if (isFormExtras(extras)) {
            return (
              <FormListPage
                formGroupId={extras.form_group_id}
                formGroupName={extras.form_group_name}
                menu={tab}
              />
            );
          }
          return <FormListPage menu={tab} />;

        case 'FORM_DASHBOARD':
          if (isFormExtras(extras)) {
            return (
              <CommunityProfile formId={extras.form_id} formName={extras.form_name} menu={tab} />
            );
          }
          return <CommunityProfile menu={tab} />;

        case 'MEETING_ROOM':
        case 'MEETING_ROOM_ROOM':
          if (isMeetingRoomExtras(extras)) {
            return (
              <Rooms
                roomId={extras.internal_id}
                roomName={tab.label}
                vendorCode={extras.vendor_code}
                roomKey={extras.key}
                background={extras.background}
                menu={tab}
                index={idx}
              />
            );
          }
          return <Rooms menu={tab} index={idx} />;

        default:
          return children;
      }
    },
    [children]
  );

  // Verifica se temos todos os menus necessários
  const hasRequiredMenus = firstLevelMenu && secondLevelMenu && thirdLevelMenus.length > 0;

  if (!isThirdLevel) {
    console.log('[ThirdLevelLayout] Não é terceiro nível, renderizando children');
    return <>{children}</>;
  }

  if (!hasRequiredMenus) {
    console.warn('[ThirdLevelLayout] Menus necessários ausentes:', {
      hasFirstLevel: !!firstLevelMenu,
      hasSecondLevel: !!secondLevelMenu,
      hasThirdLevel: thirdLevelMenus.length > 0,
    });
    return null;
  }

  const tabContents = thirdLevelMenus.map((tab, idx) => renderTabContent(tab, idx));

  return (
    <S.Container>
      <TabsCustom
        labels={thirdLevelMenus.map(tab => ({
          key: getTabKey(tab),
          label: tab.label || '',
        }))}
        activeTab={activeTab}
        content={tabContents}
        onChange={handleTabChange}
      />
    </S.Container>
  );
};

export default ThirdLevelLayout;
